import type { ResponseBaseGetOneResponseProgramPage } from "@classes-service-types/index.ts";
import type { UseQueryOptions } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { computed, type Ref, unref } from "vue";
import { learningServiceRequest } from "@/services/utils";
import { specialPricingOverrideEntityId } from "@/util/specialPricingOverride";

export const QUERY_KEY = "getProgramPageStudent";

export const useGetProgramPage = <TData = AxiosResponse<ResponseBaseGetOneResponseProgramPage>>(
  programPageId: Ref<string | null> | string,
  homeInstitutionId?: Ref<string> | string,
  domainEntityId?: Ref<string> | string,
  sessionAll?: Ref<boolean> | boolean,
  sessionId?: Ref<string | undefined> | string,
  useQueryOptions?: Partial<UseQueryOptions<AxiosResponse<ResponseBaseGetOneResponseProgramPage>, Error, TData>>,
) => {
  const queryFn = async (): Promise<AxiosResponse<ResponseBaseGetOneResponseProgramPage>> => {
    const id = unref(programPageId);
    if (!id) {
      throw new Error("programPageId is required");
    }
    // The endpoint decides which sessions are visible: it always omits cancelled and archived
    // ones, and includes Draft sessions only when session_all is set.
    const params: Record<string, string | number | boolean | undefined> = {
      home_institution_id: unref(homeInstitutionId),
      domain_entity_id: unref(domainEntityId),
      special_pricing_override_entity_id: specialPricingOverrideEntityId(),
      session_id: unref(sessionId),
      session_all: unref(sessionAll) ? true : undefined,
      sessions_lite: true,
      hide_unpriced_rooms: true,
    };
    return learningServiceRequest({
      method: "get",
      url: `/programming/program_pages/${id}/student`,
      params,
      failureMessage: "Error loading program page data",
    });
  };

  return useQuery<AxiosResponse<ResponseBaseGetOneResponseProgramPage>, Error, TData>({
    queryKey: computed(() => [
      QUERY_KEY,
      unref(programPageId),
      unref(homeInstitutionId),
      unref(domainEntityId),
      specialPricingOverrideEntityId(),
      unref(sessionAll),
      unref(sessionId),
    ]),
    queryFn,
    ...useQueryOptions,
  });
};
